import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { Add, MoreHoriz, Search } from '@mui/icons-material';
import ConfirmDialog from '../coding/ConfirmDialog';
import { deleteLesson, exportLessonCsv, listMyLessons, retryAnalysis, shareLesson } from '../../services/lessonService';
import { saveFile } from '../../services/download';

const POLL_MS = 5000;
const shortDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const titleOf = (lesson) => lesson.name || lesson.filename;

// The research-use chip repeats the choice made at upload; "used in research" marks a shared lesson
// already in annotation, adjudication or a training export (decision 2026-10-25)
export const ResearchUseChip = ({ shared, used }) => (
    <Chip
        size="small"
        label={shared ? (used ? 'used in research' : 'Shared') : 'Not shared'}
        sx={{ borderRadius: 999, fontWeight: 600, color: shared ? '#1157A3' : '#4B5565', bgcolor: shared ? '#E8F1FB' : '#EDF0F2' }}
    />
);

const StatusCell = ({ lesson, onRetry, retrying }) => {
    if (lesson.status === 'ready') return <Chip size="small" label="Ready" sx={{ borderRadius: 999, fontWeight: 600, color: '#1F6B31', bgcolor: '#E6F4EA' }} />;
    if (lesson.status === 'analysing') {
        return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <CircularProgress size={14} aria-hidden />
                <Typography variant="body2">Analysing…</Typography>
            </Box>
        );
    }
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Chip size="small" label="Failed" sx={{ borderRadius: 999, fontWeight: 600, color: '#B42318', bgcolor: '#FDECEC' }} />
            <Button size="small" onClick={(e) => { e.stopPropagation(); onRetry(lesson); }} disabled={retrying}>Try again</Button>
        </Box>
    );
};

// Frame 4: every new teacher lands here first
const Empty = () => (
    <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
        <Typography variant="h4" component="h2">No lessons yet</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 520, mx: 'auto' }}>
            Upload a classroom transcript to see who talks, which kinds of utterances they make, and how complex the reasoning is.
        </Typography>
        <Box component="ol" sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 2, sm: 4 }, flexWrap: 'wrap', listStyle: 'none', p: 0, my: 3 }}>
            {['Upload a CSV', 'Choose research use', 'Read the analysis'].map((step, i) => (
                <Box component="li" key={step} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box aria-hidden sx={{ width: 26, height: 26, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'action.selected', color: 'primary.main', fontWeight: 700, fontSize: '0.8125rem' }}>{i + 1}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{step}</Typography>
                </Box>
            ))}
        </Box>
        <Button variant="contained" size="large" component={RouterLink} to="/upload" startIcon={<Add />}>Upload a lesson</Button>
        <Box sx={{ mt: 1.5 }}><Link href="/sample-lesson.csv" download sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Download a sample file</Link></Box>
    </Paper>
);

// /dashboard, the teacher's landing page (wf-teacher-lessons 1–5, YES-265): one row per lesson with its
// analysis status and research use. No confidence scores anywhere (YES-204). Sharing is one way.
const MyLessons = () => {
    const navigate = useNavigate();
    const [lessons, setLessons] = useState(null);
    const [failed, setFailed] = useState(false);
    const [query, setQuery] = useState('');
    const [menu, setMenu] = useState(null);
    const [sharing, setSharing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState(null);
    const [retrying, setRetrying] = useState(null);
    const [notice, setNotice] = useState(null);

    const load = useCallback(async () => {
        try {
            setLessons(await listMyLessons());
            setFailed(false);
        } catch (err) {
            setFailed(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Poll while any lesson is analysing
    const analysing = lessons?.some((l) => l.status === 'analysing');
    useEffect(() => {
        if (!analysing) return undefined;
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [analysing, load]);

    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (lessons || []).filter((l) => !q || titleOf(l).toLowerCase().includes(q) || l.filename.toLowerCase().includes(q));
    }, [lessons, query]);

    const retry = async (lesson) => {
        setRetrying(lesson.lessonId);
        try {
            await retryAnalysis(lesson.lessonId);
        } catch (err) {
            if (err.response?.status !== 409) setNotice(`Couldn’t analyse ${titleOf(lesson)}. Try again later.`);
        } finally {
            setRetrying(null);
            load();
        }
    };

    const download = async (lesson) => {
        setMenu(null);
        try {
            const { blob, fileName } = await exportLessonCsv(lesson.lessonId);
            saveFile(blob, fileName || `${titleOf(lesson).replace(/\.csv$/i, '')}_codes.csv`);
        } catch (err) {
            setNotice('Couldn’t download the CSV. Try again.');
        }
    };

    const confirm = async (action, done) => {
        setBusy(true);
        setDialogError(null);
        try {
            await action();
            setSharing(null);
            setDeleting(null);
            setNotice(done);
            load();
        } catch (err) {
            setDialogError(err.response?.status === 409 ? 'This lesson changed meanwhile. Close this and look again.' : 'Something went wrong. Try again.');
        } finally {
            setBusy(false);
        }
    };

    const open = (lesson) => navigate(`/lessons/${encodeURIComponent(lesson.lessonId)}`);

    return (
        <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mt: { xs: 1, sm: 2 }, mb: 3 }}>
                <Box>
                    <Typography variant="h2" component="h1">My lessons</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Your uploaded lessons, their analysis and whether you share them for research.</Typography>
                </Box>
                {lessons?.length > 0 && <Button variant="contained" size="large" component={RouterLink} to="/upload" startIcon={<Add />}>Upload a lesson</Button>}
            </Box>

            {notice && <Alert severity="info" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}
            {failed && <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load your lessons.</Alert>}
            {!failed && lessons && lessons.length === 0 && <Empty />}

            {!failed && (!lessons || lessons.length > 0) && (
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            placeholder="Search lessons"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            inputProps={{ 'aria-label': 'Search lessons' }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                            sx={{ width: 320, maxWidth: '100%' }}
                        />
                        {lessons && <Typography variant="body2" color="text.secondary">{shown.length} {shown.length === 1 ? 'lesson' : 'lessons'}</Typography>}
                    </Box>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table aria-label="My lessons" sx={{ minWidth: 820 }}>
                            <TableHead>
                                <TableRow>
                                    {['Lesson', 'Uploaded', 'Utterances', 'Talk (teacher / student)', 'Status', 'Research use', ''].map((h, i) => (
                                        <TableCell key={i}>{h || <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Actions</Box>}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!lessons && [0, 1, 2].map((i) => <TableRow key={i}>{[0, 1, 2, 3, 4, 5, 6].map((j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
                                {shown.map((lesson) => (
                                    <TableRow
                                        key={lesson.lessonId}
                                        hover
                                        onClick={() => open(lesson)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>
                                            <Link
                                                component={RouterLink}
                                                to={`/lessons/${encodeURIComponent(lesson.lessonId)}`}
                                                onClick={(e) => e.stopPropagation()}
                                                underline="hover"
                                                sx={{ fontWeight: 700, color: 'text.primary', display: 'block' }}
                                            >
                                                {titleOf(lesson)}
                                            </Link>
                                            <Typography variant="caption" color="text.secondary">{lesson.name ? lesson.filename : 'no lesson name'}</Typography>
                                        </TableCell>
                                        <TableCell>{shortDate(lesson.uploadedAt)}</TableCell>
                                        <TableCell>{lesson.utteranceCount}</TableCell>
                                        <TableCell>{lesson.status === 'ready' && typeof lesson.teacherShare === 'number' ? `${lesson.teacherShare}% / ${100 - lesson.teacherShare}%` : '—'}</TableCell>
                                        <TableCell><StatusCell lesson={lesson} onRetry={retry} retrying={retrying === lesson.lessonId} /></TableCell>
                                        <TableCell><ResearchUseChip shared={lesson.researchUse} used={lesson.usedInResearch} /></TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                aria-label={`More for ${titleOf(lesson)}`}
                                                onClick={(e) => { e.stopPropagation(); setMenu({ anchor: e.currentTarget, lesson }); }}
                                            >
                                                <MoreHoriz />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>Click a lesson to open its analysis</Typography>
                </Paper>
            )}

            {/* Frame 2: the less frequent actions; a shared lesson can't be unshared or deleted */}
            <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ sx: { maxWidth: 280 } }}>
                {menu && [
                    <MenuItem key="open" onClick={() => { setMenu(null); open(menu.lesson); }}>Open analysis</MenuItem>,
                    <MenuItem key="csv" onClick={() => download(menu.lesson)} disabled={menu.lesson.status !== 'ready'}>Download CSV</MenuItem>,
                    ...(menu.lesson.researchUse
                        ? [<Divider key="d" />, <Typography key="note" variant="caption" component="p" color="text.secondary" sx={{ px: 2, py: 1 }}>Shared lessons can’t be unshared or deleted.</Typography>]
                        : [
                            <MenuItem key="share" onClick={() => { setDialogError(null); setSharing(menu.lesson); setMenu(null); }}>Share for research…</MenuItem>,
                            <MenuItem key="delete" onClick={() => { setDialogError(null); setDeleting(menu.lesson); setMenu(null); }} disabled={menu.lesson.status === 'analysing'} sx={{ color: 'error.main' }}>Delete lesson…</MenuItem>,
                        ]),
                ]}
            </Menu>

            <ConfirmDialog
                open={Boolean(sharing)}
                title="Share for research?"
                confirmLabel="Share for research"
                busy={busy}
                error={dialogError}
                onConfirm={() => confirm(() => shareLesson(sharing.lessonId), `${titleOf(sharing)} is shared for research.`)}
                onClose={() => setSharing(null)}
            >
                {sharing && (
                    <>
                        <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 1 }}>{titleOf(sharing)} · not shared</Typography>
                        <Typography variant="body2">The research team will be able to code this lesson, and it may be used to train the model.</Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>Sharing can’t be undone. Once a lesson has been used to train the model it can’t be taken back out, so a shared lesson can’t be unshared or deleted later.</Typography>
                    </>
                )}
            </ConfirmDialog>

            <ConfirmDialog
                open={Boolean(deleting)}
                title="Delete this lesson?"
                confirmLabel="Delete lesson"
                destructive
                busy={busy}
                error={dialogError}
                onConfirm={() => confirm(() => deleteLesson(deleting.lessonId), `${titleOf(deleting)} was deleted.`)}
                onClose={() => setDeleting(null)}
            >
                {deleting && (
                    <>
                        <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 1 }}>{[deleting.name, deleting.filename, `${deleting.utteranceCount} utterances`].filter(Boolean).join(' · ')}</Typography>
                        <Typography variant="body2">The transcript and its analysis are removed from ADAS for good. This can’t be undone; to see the analysis again you would upload the file again.</Typography>
                    </>
                )}
            </ConfirmDialog>
        </Box>
    );
};

export default MyLessons;
